/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module GslbModule */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Type,
} from '@angular/core';

import { AviContinueConfirmationComponent } from 'ng/modules/dialog';
import { ClrFormLayout } from '@clr/angular';
import { DialogService } from 'ng/modules/core/services';
import { GslbAlgorithm, GslbPoolType } from 'generated-types';
import { GslbPool } from 'object-types';
import { GslbPoolConfigItem } from 'message-items/gslb-pool.config-item.factory';
import { GslbPoolMemberConfigItem } from 'message-items/gslb-pool-member.config-item.factory';
import { GSLBService } from 'ajs/modules/gslb/factories/gslb-service/gslb-service.item.factory';
import { L10nService } from '@vmw/ngx-vip';

import './gslb-service-pool-modal.component.less';
import * as globalL10n from 'global-l10n';
import * as l10n from './gslb-service-pool-modal.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * ID for service pool modal change confirmation dialog.
 */
const SERVICE_POOL_MODAL_CHANGE_CONFIRMATION_DIALOG_ID = 'service-pool-modal-change-confirmation-dialog';

/**
 * @description Component for the GSLB Pool configuartion.
 * @author Suraj Kumar, Umesh Chovatiya, Deepak Chaudhary
 */
@Component({
    selector: 'gslb-service-pool-modal',
    templateUrl: './gslb-service-pool-modal.component.html',
})
export class GslbServicePoolModalComponent implements OnInit {
    @Input()
    public editable: GslbPoolConfigItem;

    /**
     * True if Pool is being edited.
     */
    @Input()
    public editMode = false;

    /**
     * Existing Pool names configured.
     * Pass Pool names to the directive which detects duplicate string values.
     */
    @Input()
    public poolNames: string[] = [];

    /**
     * Current Pool index.
     * Pass Pool index to the directive which detects duplicate string values.
     */
    @Input()
    public poolIndex: number;

    @Input()
    public gslbService: GSLBService;

    /**
     * Check whether the GSLB service type is SRV or not.
     */
    @Input()
    public isGSLBServiceRecordTypeSRV = false;

    @Output()
    public onCancel = new EventEmitter<void>();

    @Output()
    public onSubmit = new EventEmitter<GslbPoolConfigItem>();

    public readonly objectType = GslbPool;

    /**
     * Store hidden enum values for Fallback Algorithms dropdown.
     */
    public enumHiddenFallbackAlgorithms = [
        GslbAlgorithm.GSLB_ALGORITHM_GEO,
        GslbAlgorithm.GSLB_ALGORITHM_PREFERENCE_ORDER,
        GslbAlgorithm.GSLB_ALGORITHM_TOPOLOGY,
    ];

    /**
     * Add hidden enum values for SRV GSLB service type or empty array.
     */
    public hiddenEnumValues = [GslbAlgorithm.GSLB_ALGORITHM_GEO];

    /**
     * Flag to decide if FallbackAlgorithm configuration should be allowed.
     */
    public allowFallbackAlgorithmConfiguration = false;

    /**
     * Flag to decide if ConsistentHashMask configuration should be allowed.
     */
    public allowConsistentHashMaskConfiguration = false;

    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    private currentPoolType: GslbPoolType;

    constructor(
        private readonly l10nService: L10nService,
        private readonly dialogService: DialogService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * True if GSLB pool type is generic.
     */
    public get isGslbPoolTypeGeneric(): boolean {
        return this.editable.config.gslb_pool_type === GslbPoolType.GSLB_POOL_TYPE_GENERIC;
    }

    /** @override */
    public ngOnInit(): void {
        const { config: poolConfig } = this.editable;

        if (!this.isGSLBServiceRecordTypeSRV) {
            this.hiddenEnumValues = [];
        }

        this.allowFallbackAlgorithmConfiguration =
            poolConfig.algorithm === GslbAlgorithm.GSLB_ALGORITHM_GEO;
        this.allowConsistentHashMaskConfiguration =
            poolConfig.algorithm === GslbAlgorithm.GSLB_ALGORITHM_CONSISTENT_HASH ||
            poolConfig.fallback_algorithm === GslbAlgorithm.GSLB_ALGORITHM_CONSISTENT_HASH;
        this.currentPoolType = poolConfig.gslb_pool_type;
    }

    /**
     * Handle LoadBalancing Algorithm Change.
     */
    public handleLoadBalancingAlgorithmChange(): void {
        this.editable.onLoadBalancingAlgorithmChange();
        this.updateFlags();
    }

    /**
     * Handle GSLB Pool Type Change.
     */
    public handleGslbPoolTypeChange(): void {
        const existingPoolMemberAddresses = this.getExistingPoolMemberAddresses();

        if (existingPoolMemberAddresses.size && this.currentPoolType === GslbPoolType.GSLB_POOL_TYPE_GENERIC) {
            return this.dialogService.add({
                id: SERVICE_POOL_MODAL_CHANGE_CONFIRMATION_DIALOG_ID,
                component: AviContinueConfirmationComponent as Type<Component>,
                componentProps: {
                    warning: this.l10nService.getMessage(this.l10nKeys.changingPoolTypeWarning),
                    onConfirm: () => {
                        this.currentPoolType = this.editable.config.gslb_pool_type;
                        this.dialogService.remove(SERVICE_POOL_MODAL_CHANGE_CONFIRMATION_DIALOG_ID);
                        this.editable.onPoolTypeChange();
                    },
                    onClose: () => {
                        this.dialogService.remove(SERVICE_POOL_MODAL_CHANGE_CONFIRMATION_DIALOG_ID);
                        this.editable.config.gslb_pool_type = this.currentPoolType;
                    },
                },
            });
        }
    }

    /**
     * Handle Fallback Algorithm Change.
     */
    public handleFallbackAlgorithmChange(): void {
        this.editable.onFallbackAlgorithmChange();
        this.updateFlags();
    }

    /**
     * Update flags, used for checking if configuration content should be displayed.
     */
    public updateFlags(): void {
        const { config: poolConfig } = this.editable;

        this.allowFallbackAlgorithmConfiguration =
            poolConfig.algorithm === GslbAlgorithm.GSLB_ALGORITHM_GEO;
        this.allowConsistentHashMaskConfiguration =
            poolConfig.algorithm === GslbAlgorithm.GSLB_ALGORITHM_CONSISTENT_HASH ||
            poolConfig.fallback_algorithm === GslbAlgorithm.GSLB_ALGORITHM_CONSISTENT_HASH;
    }

    /**
     * True if GSLB algorithm is preference order.
     */
    public get isGslbAlgorithmPreferenceOrder(): boolean {
        return this.editable.config.algorithm === GslbAlgorithm.GSLB_ALGORITHM_PREFERENCE_ORDER;
    }

    /**
     * Get previous selected pool members Vs ips.
     */
    public getExistingPoolMemberAddresses(): Set<string> {
        const existingPoolMemberAddresses = new Set<string>();

        this.editable.config.members.config.forEach(member => {
            if (member.memberAddress) {
                existingPoolMemberAddresses.add(member.memberAddress);
            }
        });

        return existingPoolMemberAddresses;
    }

    /**
     * Handle add GSLB Pool member operation.
     */
    public handleAddPoolMember(): void {
        this.editable.addPoolMember(
            this.gslbService,
            this.isGSLBServiceRecordTypeSRV,
            this.isGslbAlgorithmPreferenceOrder,
            this.getExistingPoolMemberAddresses(),
            this.isGslbPoolTypeGeneric,
        );
    }

    /**
     * Handle edit GSLB Pool member operation.
     * Delete the address of the current pool member from the set to handle the validation
     * case where the user can select another VS with the same address.
     */
    public handleEditPoolMember(poolMember: GslbPoolMemberConfigItem): void {
        const existingPoolMemberAddresses = this.getExistingPoolMemberAddresses();

        if (existingPoolMemberAddresses.has(poolMember.memberAddress)) {
            existingPoolMemberAddresses.delete(poolMember.memberAddress);
        }

        this.editable.editPoolMember(
            poolMember,
            this.gslbService,
            this.isGSLBServiceRecordTypeSRV,
            this.isGslbAlgorithmPreferenceOrder,
            existingPoolMemberAddresses,
            this.isGslbPoolTypeGeneric,
        );
    }

    /**
     * Handle GSLB Pool Submit.
     */
    public submit(): void {
        this.onSubmit.emit(this.editable);
    }

    /**
     * Handle GSLB Pool Cancel.
     */
    public cancel(): void {
        this.onCancel.emit();
    }
}
